import * as React from "react";
import { StoreContext } from "src/context/store-context";
import { NumericInput } from "src/components/Shopify/NumericInput";
import { formatPrice } from "src/utils/format-price";
import isEqual from "lodash.isequal";
import Heading from "./Heading";
import SbEditable from "storyblok-react";
import { useForm } from "react-hook-form";

export default function Product({ blok }) {
  const { register, handleSubmit } = useForm();

  const { client } = React.useContext(StoreContext);
  let { product } = blok;
  const {
    options,
    variants: [initialVariant],
    title,
    descriptionHtml,
    images,
    images: [firstImage],
  } = product.item;

  const [variant, setVariant] = React.useState({ ...initialVariant });
  const [quantity, setQuantity] = React.useState(1);

  const productVariant =
    client.product.helpers.variantForOptions(product.item, variant) || variant;

  const [available, setAvailable] = React.useState(
    productVariant.availableForSale
  );

  const checkAvailablity = React.useCallback(
    (productId) => {
      client.product.fetch(productId).then((fetchedProduct) => {
        const result =
          fetchedProduct?.variants.filter(
            (variant) => variant.id === productVariant.id
          ) ?? [];

          // console.log(fetchedProduct, productVariant)
          // console.log(result)

        if (result.length > 0) {
          setAvailable(result[0].available);
        }
      });
    },
    [productVariant, client.product]
  );

  const handleOptionChange = (index, event) => {
    const value = event.target.value;

    if (value === "") {
      return;
    }

    const currentOptions = [...variant.selectedOptions];

    currentOptions[index] = {
      ...currentOptions[index],
      value,
    };

    const selectedVariant = product.item.variants.find((variant) => {
      return isEqual(currentOptions, variant.selectedOptions);
    });

    setVariant({ ...selectedVariant });
  };

  React.useEffect(() => {
    checkAvailablity(product.item.id);
  }, [productVariant.id, checkAvailablity, product.item.id]);

  const price = formatPrice(variant.price.currencyCode, variant.price.amount);

  const hasVariants = product.item.variants.length > 1;
  const hasImages = images.length > 0;

  const onSubmit = (data) => {
    const customAttributes = data.delegates
      ?.filter((item) => item.name !== "")
      .map((delegate, index) => {
        return {
          key: `Delegate ${index + 1}`,
          value: `${delegate.name}, ${delegate.email_address}, ${delegate.company}`,
        };
      });
    addVariantToCart(variant.id, quantity, customAttributes);
  };

  const { addVariantToCart, loading } = React.useContext(StoreContext);

  // console.log(available)

  return (
    <SbEditable content={blok} key={blok._uid}>
      <div className="mx-auto max-w-7xl py-16 px-6">
        <form className="mt-10" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col items-start gap-10 lg:flex-row lg:gap-20">
            {hasImages && (
              <div className="w-full lg:w-3/5">
                <Heading
                  size="h1"
                  className="font-display text-darkgrey mb-2 mb-3 text-4xl uppercase !leading-none tracking-normal lg:hidden lg:text-5xl"
                >
                  {title}
                </Heading>
                <div className="text-red font-display mb-2 text-lg lg:hidden">
                  {price}
                </div>
                <div
                  role="group"
                  aria-label="gallery"
                  aria-describedby="instructions"
                >
                  <ul>
                    {images.map((image, index) => (
                      <li key={`product-image-${image.id}`}>
                        <img
                          // objectFit="contain"
                          loading={index === 0 ? "eager" : "lazy"}
                          alt={
                            image.altText
                              ? image.altText
                              : `Product Image of ${title} #${index + 1}`
                          }
                          src={image.src}
                          className="w-full"
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {!hasImages && <span>No Preview image</span>}
            <div className="flex w-full flex-col justify-center lg:w-2/5">
              <Heading
                size="h1"
                className="font-display text-darkgrey mb-2 mb-3 hidden text-4xl uppercase !leading-none tracking-normal lg:inline-block lg:text-5xl"
              >
                {title}
              </Heading>
              <div className="text-red font-display mb-4 hidden text-lg lg:inline-block">
                {price}
              </div>
              <div
                className="prose text-darkgrey mb-8 text-sm"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              ></div>
              <div className="flex flex-col gap-3">
                <fieldset className="grid grid-cols-1">
                  {hasVariants &&
                    options.map(({ id, name, values }, index) => (
                      <div key={id}>
                        <select
                          aria-label="Variants"
                          onChange={(event) => handleOptionChange(index, event)}
                          className="placeholder-placeholdergrey focus:ring-darkred focus:border-darkred font-display block w-full border-[#e0e0e0] py-3 px-4 text-lg shadow-sm"
                        >
                          <option value="">{`Select ${name}`}</option>
                          {values.map(({ value }) => (
                            <option
                              value={value}
                              key={`${name}-${value}`}
                              selected={
                                value === variant.title ? "selected" : ""
                              }
                            >
                              {value}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                </fieldset>
                <NumericInput
                  aria-label="Quantity"
                  onIncrement={() => setQuantity((q) => Math.min(q + 1, 20))}
                  onDecrement={() => setQuantity((q) => Math.max(1, q - 1))}
                  onChange={(event) => setQuantity(event.currentTarget.value)}
                  value={quantity}
                  min="1"
                  max="20"
                />

                <button
                  type="submit"
                  disabled={!available || loading}
                  className={`line-crop font-display inline-flex w-full max-w-xs items-center justify-center px-[30px] py-[15px] text-lg uppercase !leading-[0] tracking-normal text-white transition duration-300 ${
                    available
                      ? "bg-red hover:bg-darkred cursor-pointer"
                      : "cursor-not-allowed bg-gray-400 hover:bg-gray-500 "
                  }`}
                >
                  {available ? "Add to Basket" : "Out of Stock"}
                  <i className="fas fa-chevron-right ml-3 inline text-xs"></i>
                </button>
              </div>
              <div className="w-full bg-darkgrey lg:-mx-8 sm:mt-10 mt-6 max-w-xl">
                {product.item.productType === "Course" && (
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 sm:p-8 p-6">
                    <div className="col-span-full">
                      <Heading
                        size="h5"
                        className="text-white mb-2 !leading-none"
                      >
                        Tell us about you
                      </Heading>
                      <p className="text-white prose text-sm">
                        Enter your delegate information below - You'll need to add
                        these details before adding to your basket
                      </p>
                    </div>
                    {Array.from({ length: quantity }).map((_, index) => {
                      return (
                        <div
                          key={index}
                          className="col-span-full grid grid-cols-2 gap-x-4"
                        >
                          <label className="text-white font-display col-span-2 mb-1 text-sm uppercase">{`Delegate ${
                            index + 1
                          }`}</label>
                          <input
                            {...register(`delegates.${index}.name`, {
                              required: true,
                            })}
                            type="text"
                            className="placeholder-placeholdergrey focus:ring-darkred focus:border-darkred font-display mb-4 block w-full border-[#e0e0e0] py-3 px-4 text-lg shadow-sm"
                            placeholder="Name"
                          />
                          <input
                            {...register(`delegates.${index}.company`, {
                              required: true,
                            })}
                            type="text"
                            className="placeholder-placeholdergrey focus:ring-darkred focus:border-darkred font-display mb-4 block w-full border-[#e0e0e0] py-3 px-4 text-lg shadow-sm"
                            placeholder="Company"
                          />
                          <input
                            {...register(`delegates.${index}.email_address`, {
                              required: true,
                            })}
                            type="text"
                            className="placeholder-placeholdergrey focus:ring-darkred focus:border-darkred font-display col-span-2 block w-full border-[#e0e0e0] py-3 px-4 text-lg shadow-sm"
                            placeholder="Email address"
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </SbEditable>
  );
}
